import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ADMIN_URL_DATA_MANAGMENT } from "../components/Admin/PageStructure/admin-values";
import { appendRoute, RouteHistoryEntry } from "../support/slices/uiSlice";
import { RootState } from "../support/store";
import { useUserRegistry } from "./useUserRegistry";

const usePopulateHistory = () => {
  const dispatch = useDispatch();
  const { routeHistory: uiSliceHistory } = useSelector((state: RootState) => state.ui);
  const { 
    registryReady,
    userHistory: registryHistory,
  } = useUserRegistry();

  useEffect(() => {
    if (!registryReady) {
      return;
    }
    if (uiSliceHistory.length < 2) { // current route might already be added if reloaded on a watched component
      (registryHistory ?? []).forEach((item: RouteHistoryEntry) => {
        dispatch(appendRoute(item));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryReady]);
}

const useNavigationHistory = (displayTitle?: string) => {
  const location = useLocation();
  const { id, section } = useParams();
  const [ searchParams ] = useSearchParams();
  const dispatch = useDispatch();
  const { routeHistory: uiSliceHistory } = useSelector((state: RootState) => state.ui);
  const { 
    registry,
    userHistory: registryHistory,
    updateUserHistory,
  } = useUserRegistry();

  useEffect(() => {
    if (!registry) {
      return;
    }

    const historyUpdated = JSON.stringify(registryHistory) !== JSON.stringify(uiSliceHistory);
    if (historyUpdated) {
      const saveHistoryTimerId = setTimeout(() => {
        updateUserHistory(uiSliceHistory);
      }, 10000);
      return () => clearTimeout(saveHistoryTimerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiSliceHistory]);

  useEffect(() => {
    let currentSection = section;
    if (!currentSection) {
      // admin: products and gtm won't be picked up because they have fixed routes with children
      if (location.pathname.includes(`${ADMIN_URL_DATA_MANAGMENT}/products`)) {
        currentSection = 'products';
      }
      if (location.pathname.includes(`${ADMIN_URL_DATA_MANAGMENT}/goToMarkets`)) {
        currentSection = 'goToMarkets';
      }
    }
    dispatch(
      appendRoute({
        route: `${location.pathname}${location.search}`,
        params: {id, section, ...Object.fromEntries(searchParams)},
        title: displayTitle,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, section, id]);
}

export default useNavigationHistory;
export { usePopulateHistory };